import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, IconButton, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import { OptifundDashboardTab } from "../../../../features/optifundDashboardSlice";
import { setDisplayErrorSnackBar } from "../../../../features/userSlice";
import { downloadCsvSections } from "./csvExport";
import Performance, { PerformanceExportHandle } from "./Performance/Performance";
import Brands, { BrandsExportHandle } from "./Brands/Brands";
import Users, { UsersExportHandle } from "./Users/Users";

interface OptifundDashboardTabsProps {
  userToken: any;
  websiteSharedUid: string;
}

function isOptifundDashboardTab(value: string | null): value is OptifundDashboardTab {
  return value === "performance" || value === "brands" || value === "users";
}

export default function OptifundDashboardTabs({ userToken, websiteSharedUid }: OptifundDashboardTabsProps) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedTab, setSelectedTab] = useState<OptifundDashboardTab>(() => {
    const tab = searchParams.get("tab");
    return isOptifundDashboardTab(tab) ? tab : "performance";
  });
  // Each tab lazy-mounts on first visit and then stays mounted (hidden via
  // display:none rather than unmounted) so switching back to it doesn't
  // re-trigger its fetch.
  const [visitedTabs, setVisitedTabs] = useState<OptifundDashboardTab[]>(() =>
    selectedTab === "performance" ? ["performance"] : ["performance", selectedTab]
  );
  // Not URL-synced - a refresh is a one-off action, not part of a
  // shareable view. Keyed per tab (rather than one shared counter) so
  // refreshing only remounts - and therefore only refetches - the tab
  // that's currently selected, not all three.
  const [refreshKeys, setRefreshKeys] = useState<Record<OptifundDashboardTab, number>>({
    performance: 0,
    brands: 0,
    users: 0,
  });

  const performanceRef = useRef<PerformanceExportHandle>(null);
  const brandsRef = useRef<BrandsExportHandle>(null);
  const usersRef = useRef<UsersExportHandle>(null);

  const handleChange = (_event: SyntheticEvent, newTab: OptifundDashboardTab) => {
    setSelectedTab(newTab);
    setVisitedTabs((current) => (current.includes(newTab) ? current : [...current, newTab]));
  };

  // Mirrors the selected tab to the URL, preserving any other query params
  // already present, without piling up history entries. The default
  // ("performance") is omitted entirely rather than written out.
  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (selectedTab === "performance") next.delete("tab");
        else next.set("tab", selectedTab);
        return next;
      },
      { replace: true }
    );
  }, [selectedTab, setSearchParams]);

  // Each tab is keyed on its own entry in refreshKeys (below), so bumping
  // just the selected tab's key remounts only that tab - a fresh mount
  // re-runs its useOptifundTabData effects from scratch, forcing a real
  // refetch that bypasses its otherwise-permanent keep-alive cache, while
  // leaving the other two tabs' already-fetched data untouched. Filter/
  // sort/pagination state all lives in each tab's own local state (see
  // docs/new-module-checklist.md Section 5), so nothing is lost by
  // remounting.
  const handleRefresh = () => {
    setRefreshKeys((keys) => ({ ...keys, [selectedTab]: keys[selectedTab] + 1 }));
  };

  const handleExport = () => {
    const exportRefByTab: Record<OptifundDashboardTab, React.RefObject<{ getExportSections: () => any }>> = {
      performance: performanceRef,
      brands: brandsRef,
      users: usersRef,
    };
    const sections = exportRefByTab[selectedTab].current?.getExportSections();
    if (!sections) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "This tab hasn't finished loading yet - please wait and try again.",
          type: "error",
        })
      );
      return;
    }
    downloadCsvSections(`optifund-${selectedTab}-${dayjs().format("YYYY-MM-DD")}.csv`, sections);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Stack direction="row" alignItems="flex-end" spacing={1.75}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700} sx={{ color: "#1a1a1a" }}>
            Optifund Dashboard
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: "#757575" }}>
            Business health at a glance - acquisition performance, brand payouts,
            and the applicant funnel.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton color="secondary" onClick={handleRefresh} data-tracker="970">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export">
          <IconButton color="secondary" onClick={handleExport} data-tracker="971">
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Tabs
        value={selectedTab}
        onChange={handleChange}
        textColor="secondary"
        indicatorColor="secondary"
        sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="PERFORMANCE" value="performance" sx={{ fontWeight: 500, letterSpacing: "0.6px" }} />
        <Tab label="BRANDS" value="brands" sx={{ fontWeight: 500, letterSpacing: "0.6px" }} />
        <Tab label="USERS" value="users" sx={{ fontWeight: 500, letterSpacing: "0.6px" }} />
      </Tabs>

      <Box sx={{ mt: 2.5, display: selectedTab === "performance" ? "block" : "none" }}>
        {visitedTabs.includes("performance") && (
          <Performance key={refreshKeys.performance} ref={performanceRef} userToken={userToken} websiteSharedUid={websiteSharedUid} />
        )}
      </Box>
      <Box sx={{ mt: 2.5, display: selectedTab === "brands" ? "block" : "none" }}>
        {visitedTabs.includes("brands") && (
          <Brands key={refreshKeys.brands} ref={brandsRef} userToken={userToken} websiteSharedUid={websiteSharedUid} />
        )}
      </Box>
      <Box sx={{ mt: 2.5, display: selectedTab === "users" ? "block" : "none" }}>
        {visitedTabs.includes("users") && (
          <Users key={refreshKeys.users} ref={usersRef} userToken={userToken} websiteSharedUid={websiteSharedUid} />
        )}
      </Box>
    </Box>
  );
}
